import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  ClassesPage,
  CoursesPage,
  RoomsPage,
  TeachersPage
} from "../features/catalog/CatalogPages";
import {
  TimeSlotsPage,
  UnavailabilityPage
} from "../features/calendar/CalendarPages";
import { PlanningPage } from "../features/plans/PlanningPage";
import { CoverPage } from "../features/projects/CoverPage";
import { ProjectHome } from "../features/projects/ProjectHome";
import { ProjectsPage } from "../features/projects/ProjectsPage";
import { TimetablePage } from "../features/timetable/TimetablePage";
import { getProjectRepository } from "../data/projectRepository";

function StorageGate({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [state, setState] = useState<
    { status: "checking" | "ready" | "error"; message?: string }
  >({ status: "checking" });

  useEffect(() => {
    let active = true;
    void getProjectRepository().then(
      () => active && setState({ status: "ready" }),
      (error: unknown) =>
        active &&
        setState({
          status: "error",
          message:
            error instanceof Error
              ? error.message
              : "无法使用当前浏览器的本地课表存储"
        })
    );
    return () => {
      active = false;
    };
  }, []);

  if (state.status === "checking") {
    return (
      <main className="page-shell">
        <p className="state-message">正在打开当前浏览器中的课表…</p>
      </main>
    );
  }
  if (state.status === "error") {
    return (
      <main className="page-shell">
        <section className="empty-card compact" role="alert">
          <p className="eyebrow">本地数据暂不可用</p>
          <h1>无法安全打开课表</h1>
          <p>{state.message}</p>
          <p>请确认没有禁用本站存储，或改用最新版 Chrome、Edge 后重试。现有课表没有被改动。</p>
        </section>
      </main>
    );
  }
  return <>{children}</>;
}

function CurrentRoute(): React.JSX.Element {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const projectMatch = path.match(/^\/projects\/([0-9a-f-]+)$/i);
  if (projectMatch?.[1]) return <ProjectHome projectId={projectMatch[1]} />;
  const featureMatch = path.match(
    /^\/projects\/([0-9a-f-]+)\/(courses|rooms|teachers|classes|time_slots|unavailability|plans)$/i
  );
  if (featureMatch?.[1] && featureMatch[2] === "courses") {
    return <CoursesPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "rooms") {
    return <RoomsPage projectId={featureMatch[1]} />;
  }
  if (
    featureMatch?.[1] &&
    featureMatch[2] === "teachers"
  ) {
    return <TeachersPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "classes") {
    return <ClassesPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "time_slots") {
    return <TimeSlotsPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "unavailability") {
    return <UnavailabilityPage projectId={featureMatch[1]} />;
  }
  if (featureMatch?.[1] && featureMatch[2] === "plans") {
    return <PlanningPage projectId={featureMatch[1]} />;
  }
  const timetableMatch = path.match(
    /^\/projects\/([0-9a-f-]+)\/timetable\/(classes|teachers|rooms)$/i
  );
  if (timetableMatch?.[1] && timetableMatch[2]) {
    return (
      <TimetablePage
        projectId={timetableMatch[1]}
        view={
          timetableMatch[2] === "classes"
            ? "CLASS"
            : timetableMatch[2] === "teachers"
              ? "TEACHER"
              : "ROOM"
        }
      />
    );
  }
  if (path === "/projects") return <ProjectsPage />;
  return (
    <main className="page-shell">
      <a className="back-link" href="/projects">← 返回课表项目</a>
      <section className="empty-card compact">
        <h1>页面不存在</h1>
      </section>
    </main>
  );
}

export function App(): React.JSX.Element {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { retry: 1, staleTime: 15_000 }
        }
      })
  );

  const isCover = (window.location.pathname.replace(/\/+$/, "") || "/") === "/";
  // 纯展示判断：位于某个项目内时，顶栏右侧提供返回项目库的入口
  const inProject = /^\/projects\/[0-9a-f-]+/i.test(window.location.pathname);

  return (
    <QueryClientProvider client={client}>
      {isCover ? (
        <CoverPage />
      ) : (
        <div className="app-frame">
          <header className="app-topbar">
            <div className="app-topbar-inner">
              <a className="brand-link" href="/" aria-label="七维排课助手首页">
                <span className="brand-icon" aria-hidden="true">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="17" rx="2" />
                    <path d="M3 9h18M8 2v4M16 2v4M7 13h4M7 17h7" />
                  </svg>
                </span>
                <span className="brand-name">七维排课助手</span>
              </a>
              <a
                className="topbar-mission"
                href="https://qiwiedu.com/"
                target="_blank"
                rel="noreferrer"
              >
                七维教育，支持每一位特殊儿童的全生涯发展。
              </a>
              {inProject ? (
                <a className="topbar-back" href="/projects">
                  ← 全部课表项目
                </a>
              ) : null}
            </div>
          </header>
          <StorageGate>
            <CurrentRoute />
          </StorageGate>
        </div>
      )}
    </QueryClientProvider>
  );
}
